import type { ChatSource } from "@gb/contracts";
import { MarkdownView } from "@/components/gb/markdown-view";
import { cn } from "@/lib/utils";

/**
 * An answer, with its citations made clickable.
 *
 * The prose is markdown — `MarkdownView` handles it, and the reasoning about
 * why that is a hand-rolled subset rather than a dependency lives there. This
 * file is only the one construct that is ours: the model writes `[[cite:N]]`,
 * and each becomes a numbered chip that jumps to the matching source card.
 *
 * A marker whose number has no source is dropped. The server strips those from
 * what it *stores*, and this is the same guard for text still arriving on the
 * wire, where a marker may be half written.
 */
export function AnswerBody({
  text,
  sources,
  onCite,
}: {
  text: string;
  sources: readonly ChatSource[];
  /** Called with a source number when its chip is clicked. */
  onCite: (n: number) => void;
}) {
  const known = new Set(sources.map((source) => source.n));

  return (
    <MarkdownView
      text={text}
      trust="model"
      className="text-[0.95rem] leading-[1.65] text-ink-prose"
      renderMarker={({ name, arg }) => {
        if (name !== "cite") return null;
        const n = Number(arg);
        // An unknown number renders as nothing at all rather than as a dead
        // chip: an unclickable [4] over a source list that stops at 3 reads as a bug
        // the user can do nothing about.
        if (!Number.isInteger(n) || !known.has(n)) return null;
        return <CiteChip n={n} onCite={onCite} />;
      }}
    />
  );
}

function CiteChip({ n, onCite }: { n: number; onCite: (n: number) => void }) {
  return (
    <button
      type="button"
      onClick={() => onCite(n)}
      aria-label={`Show source ${n}`}
      className={cn(
        "mx-[2px] inline-flex h-[18px] min-w-[18px] cursor-pointer items-center justify-center",
        "rounded-full border border-brand/40 bg-brand/10 px-[5px] align-[1px]",
        "font-display text-[0.66rem] font-semibold text-brand",
        "transition-colors hover:border-brand hover:bg-brand hover:text-white",
      )}
    >
      {n}
    </button>
  );
}
